import { useCallback } from 'react';

export type OfflineOperationMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type OfflineOperationStatus =
  | 'pending'
  | 'processing'
  | 'success'
  | 'failed';

export interface OfflineOperation {
  id: string;
  createdAt: number;
  method: OfflineOperationMethod;
  url: string;
  body: any;
  headers?: Record<string, string>;
  entityType: string;
  tempId?: string;
  status: OfflineOperationStatus;
  lastError?: string | null;
  lastTriedAt?: number | null;
}

interface OfflineQueueState {
  operations: OfflineOperation[];
}

const STORAGE_KEY = 'offlineOperationsQueue';

const readQueue = (): OfflineQueueState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { operations: [] };
    }
    const parsed = JSON.parse(raw) as OfflineQueueState;
    if (!parsed.operations) {
      return { operations: [] };
    }
    return parsed;
  } catch (error) {
    console.error('Error reading offline operations queue:', error);
    return { operations: [] };
  }
};

const writeQueue = (state: OfflineQueueState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error writing offline operations queue:', error);
  }
};

const createId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    // @ts-ignore
    return crypto.randomUUID() as string;
  }
  return `offline-op-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const getOfflineQueue = (): OfflineOperation[] => {
  return readQueue().operations;
};

export const hasPendingDeleteForMeasurement = (
  measurementUuid: string
): boolean => {
  const operations = readQueue().operations;

  return operations.some((op) => {
    if (
      op.entityType !== 'measurement' ||
      op.method !== 'DELETE' ||
      (op.status !== 'pending' && op.status !== 'failed')
    ) {
      return false;
    }

    try {
      const url = new URL(op.url, 'http://dummy');
      const uuidFromUrl = url.searchParams.get('measurementUuid');
      return uuidFromUrl === measurementUuid;
    } catch {
      return op.url.includes(`measurementUuid=${measurementUuid}`);
    }
  });
};

export const getPendingOfflineOperationsCount = (): number => {
  return readQueue().operations.filter(
    (op) => op.status === 'pending' || op.status === 'failed'
  ).length;
};

export const enqueueOfflineOperation = (input: {
  method: OfflineOperationMethod;
  url: string;
  body: any;
  entityType: string;
  tempId?: string;
}): OfflineOperation => {
  const state = readQueue();

  const operation: OfflineOperation = {
    id: createId(),
    createdAt: Date.now(),
    method: input.method,
    url: input.url,
    body: input.body,
    headers: undefined,
    entityType: input.entityType,
    tempId: input.tempId,
    status: 'pending',
    lastError: null,
    lastTriedAt: null,
  };

  const next: OfflineQueueState = {
    operations: [...state.operations, operation],
  };

  writeQueue(next);
  return operation;
};

// Upsert do kolejki: jeśli istnieje pasująca operacja (dedupeKey), to ją nadpisujemy
// zamiast dodawać kolejną. Dzięki temu cofnięcie kroku i ponowny zapis nie tworzy dubli.
export const upsertOfflineOperation = (input: {
  method: OfflineOperationMethod;
  url: string;
  body: any;
  entityType: string;
  tempId?: string;
  dedupeKey: string;
}): OfflineOperation => {
  const state = readQueue();

  const existing = state.operations
    .filter((op) => op.status === 'pending' || op.status === 'failed')
    .find((op) => (op as any).dedupeKey === input.dedupeKey);

  if (existing) {
    const updated: OfflineOperation = {
      ...existing,
      method: input.method,
      url: input.url,
      body: input.body,
      entityType: input.entityType,
      tempId: existing.tempId ?? input.tempId,
      createdAt: Date.now(),
      status: 'pending',
      lastError: null,
      lastTriedAt: null,
    };

    const next = state.operations.map((op) =>
      op.id === existing.id ? ({ ...updated, dedupeKey: input.dedupeKey } as any) : op
    );
    writeQueue({ operations: next });
    return updated;
  }

  // dodajemy nową, zapisując też dedupeKey
  const operation = enqueueOfflineOperation({
    method: input.method,
    url: input.url,
    body: input.body,
    entityType: input.entityType,
    tempId: input.tempId,
  });

  updateOfflineOperation(operation.id, { ...(operation as any), dedupeKey: input.dedupeKey } as any);
  return operation;
};

export const updateOfflineOperation = (
  id: string,
  patch: Partial<OfflineOperation>
): void => {
  const state = readQueue();
  const operations = state.operations.map((op) =>
    op.id === id ? { ...op, ...patch } : op
  );
  writeQueue({ operations });
};

export const removeOfflineOperation = (id: string): void => {
  const state = readQueue();
  const operations = state.operations.filter((op) => op.id !== id);
  writeQueue({ operations });
};

export const clearOfflineQueue = (): void => {
  writeQueue({ operations: [] });
};

export const useOfflineQueue = () => {
  const getQueue = useCallback(() => getOfflineQueue(), []);

  const getPendingCount = useCallback(
    () => getPendingOfflineOperationsCount(),
    []
  );

  return {
    getQueue,
    getPendingCount,
  };
};
